import { Input, Popover } from "antd";
import { IconType } from "react-icons";
import * as TbIcons from "react-icons/tb";
import { memo, useMemo, useState } from "react";
import { GroupFormValue } from "./ConfigTemplates";
import { hashString } from "utils/colors";

export const ICON_TRAY_NAMES = [
  "TbCircleDashed",
  "TbAlertCircle",
  "TbArchive",
  "TbBolt",
  "TbBookmark",
  "TbBriefcase",
  "TbCalendar",
  "TbCheck",
  "TbClipboard",
  "TbClock",
  "TbCoin",
  "TbCreditCard",
  "TbEye",
  "TbFile",
  "TbFileText",
  "TbFlag",
  "TbFolder",
  "TbHeart",
  "TbHourglass",
  "TbInbox",
  "TbList",
  "TbLock",
  "TbMail",
  "TbMessage",
  "TbPhone",
  "TbPin",
  "TbProgress",
  "TbRocket",
  "TbSearch",
  "TbSend",
  "TbShield",
  "TbStar",
  "TbTag",
  "TbTarget",
  "TbTrophy",
  "TbUser",
  "TbUsers",
  "TbWand",
];

export const COLUMN_ICONS: Record<string, IconType> = Object.fromEntries(
  ICON_TRAY_NAMES.map((name) => [name, TbIcons[name as keyof typeof TbIcons]]),
);

const renderColumnIcon = (name: string | undefined, size = 16, color?: string) => {
  const Icon = (name && COLUMN_ICONS[name]) || TbIcons.TbCircleDashed;
  return <Icon size={size} color={color} />;
};

export const pickRandomIcon = () => {
  const entries = Object.entries(COLUMN_ICONS);
  const index = Math.floor(Math.random() * entries.length);
  return entries[index][0];
};

interface IconPickerProps {
  value?: string;
  onChange?: (val: string) => void;
  accentColor?: string;
}

export const IconPicker = memo(function IconPicker({ value, onChange, accentColor }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const entries = useMemo(() => {
    const all = Object.entries(COLUMN_ICONS);
    if (!search.trim()) return all;
    const q = search.toLowerCase().replace(/^tb/, "");
    return all.filter(([key]) => key.toLowerCase().replace(/^tb/, "").includes(q));
  }, [search]);

  const content = (
    <div style={{ width: 240 }}>
      <Input
        size="small"
        placeholder="Search icons"
        prefix={<TbIcons.TbSearch size={12} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
        autoFocus
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          maxHeight: 220,
          overflowY: "auto",
        }}
      >
        {entries.map(([key, Icon]) => {
          const selected = key === value;
          return (
            <button
              key={key}
              type="button"
              title={key.replace(/^Tb/, "")}
              onClick={() => {
                onChange?.(key);
                setOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                border: selected ? `1px solid ${accentColor ?? "#6d28d9"}` : "1px solid transparent",
                borderRadius: 6,
                background: selected ? `${accentColor ?? "#6d28d9"}14` : "transparent",
                color: selected ? (accentColor ?? "#6d28d9") : "#374151",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Icon size={15} />
            </button>
          );
        })}
        {entries.length === 0 && (
          <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "#9ca3af", fontSize: 12, padding: 8 }}>
            No icons found
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      destroyTooltipOnHide
    >
      <button
        type="button"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 8,
          border: `1px solid ${accentColor ? `${accentColor}33` : "#e5e7eb"}`,
          background: accentColor ? `${accentColor}10` : "#fff",
          color: accentColor ?? "#6b7280",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        {renderColumnIcon(value, 18, accentColor)}
      </button>
    </Popover>
  );
});

export const GROUP_COLOR_PALETTE = [
  { solid: "#8b5cf6", soft: "#ede9fe", border: "#ddd6fe" },
  { solid: "#6366f1", soft: "#e0e7ff", border: "#c7d2fe" },
  { solid: "#a855f7", soft: "#f3e8ff", border: "#e9d5ff" },
  { solid: "#ec4899", soft: "#fce7f3", border: "#fbcfe8" },
  { solid: "#f43f5e", soft: "#ffe4e6", border: "#fecdd3" },
  { solid: "#ef4444", soft: "#fee2e2", border: "#fecaca" },
  { solid: "#f97316", soft: "#ffedd5", border: "#fed7aa" },
  { solid: "#f59e0b", soft: "#fef3c7", border: "#fde68a" },
  { solid: "#eab308", soft: "#fef9c3", border: "#fef08a" },
  { solid: "#84cc16", soft: "#ecfccb", border: "#d9f99d" },
  { solid: "#10b981", soft: "#d1fae5", border: "#a7f3d0" },
  { solid: "#14b8a6", soft: "#ccfbf1", border: "#99f6e4" },
  { solid: "#06b6d4", soft: "#cffafe", border: "#a5f3fc" },
  { solid: "#0ea5e9", soft: "#e0f2fe", border: "#bae6fd" },
  { solid: "#3b82f6", soft: "#dbeafe", border: "#bfdbfe" },
  { solid: "#64748b", soft: "#f1f5f9", border: "#e2e8f0" },
];

export const getPaletteEntry = (color: string | undefined, fallbackIdx: number) => {
  const found = GROUP_COLOR_PALETTE.find((p) => p.solid === color);
  return found ?? GROUP_COLOR_PALETTE[fallbackIdx % GROUP_COLOR_PALETTE.length];
};

export const pickColorFromSeed = (seed: string) => {
  const index = hashString(seed) % GROUP_COLOR_PALETTE.length;
  const selected = GROUP_COLOR_PALETTE[index];
  return selected;
};

export const pickGroupColor = (existingGroups: GroupFormValue[] = []): string => {
  const used = new Set(existingGroups.map((g) => g.meta?.color).filter(Boolean) as string[]);
  const available = GROUP_COLOR_PALETTE.filter((c) => !used.has(c.solid));
  const pool = available.length ? available : GROUP_COLOR_PALETTE;
  return pool[Math.floor(Math.random() * pool.length)].solid;
};

interface ColorPickerProps {
  value?: string;
  onChange?: (val: string) => void;
}

export const ColorPicker = memo(function ColorPicker({ value, onChange }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const palette = getPaletteEntry(value, 0);

  const content = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(8, 1fr)",
        gap: 6,
        width: 224,
      }}
    >
      {GROUP_COLOR_PALETTE.map((c) => {
        const selected = c.solid === value;
        return (
          <button
            key={c.solid}
            type="button"
            onClick={() => {
              onChange?.(c.solid);
              setOpen(false);
            }}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: c.solid,
              border: "none",
              cursor: "pointer",
              padding: 0,
              boxShadow: selected ? `0 0 0 2px #fff, 0 0 0 4px ${c.solid}` : "none",
              transition: "box-shadow 0.12s",
            }}
            aria-label={`Select color ${c.solid}`}
          />
        );
      })}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      destroyTooltipOnHide
    >
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: palette.solid,
          boxShadow: `0 0 0 3px ${palette.soft}`,
          border: "none",
          padding: 0,
          cursor: "pointer",
          flexShrink: 0,
        }}
        aria-label="Change group color"
      />
    </Popover>
  );
});
